import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  BookOpen, TrendingUp, TrendingDown, Filter, RefreshCw,
  ChevronDown, ChevronRight, Clock, Target, Zap,
  Activity, BarChart3, AlertTriangle
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

export default function TradeJournal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterSymbol, setFilterSymbol] = useState('');
  const [expandedTrade, setExpandedTrade] = useState(null);
  const { t } = useTranslation();

  const fetchJournal = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterAgent) params.agent_id = filterAgent;
      if (filterSymbol) params.symbol = filterSymbol;
      const res = await api.get('/api/journal/trades', { params });
      setData(res.data);
    } catch (err) {
      console.error('Error fetching journal:', err);
    }
    setLoading(false);
  }, [filterAgent, filterSymbol]);

  useEffect(() => { fetchJournal(); }, [fetchJournal]);

  const summary = data?.summary || {};
  const tca = data?.tca_summary || {};
  const trades = data?.trades || [];
  const agentBreakdown = data?.agent_breakdown || [];
  const stratBreakdown = data?.strategy_breakdown || [];

  // Get unique agents/symbols for filters
  const uniqueAgents = [...new Set(trades.map(t => t.agent_id))].sort();
  const uniqueSymbols = [...new Set(trades.map(t => t.symbol))].sort();

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#030508] flex items-center justify-center" data-testid="trade-journal">
        <div className="text-center">
          <BookOpen className="w-10 h-10 text-emerald-500 animate-pulse mx-auto mb-3" />
          <p className="text-slate-400 font-mono text-sm">{t('journal.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="trade-journal">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#030508]/90 backdrop-blur-xl border-b border-white/[0.08] px-4 md:px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-5 h-5 text-emerald-500" />
            <h1 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('journal.title')}</h1>
            <span className="text-[10px] font-mono text-slate-500 bg-white/5 px-2 py-0.5">{trades.length} trades</span>
          </div>
          <button onClick={fetchJournal} className="text-slate-400 hover:text-white transition-colors" data-testid="refresh-journal-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <main className="p-4 md:p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="journal-summary">
          <SummaryCard label="P&L Total" value={`$${summary.total_pnl?.toLocaleString()}`} color={summary.total_pnl >= 0 ? 'emerald' : 'red'} />
          <SummaryCard label="Win Rate" value={`${summary.win_rate}%`} color={summary.win_rate >= 50 ? 'emerald' : 'amber'} />
          <SummaryCard label={t('journal.winners')} value={summary.winning_trades} color="emerald" />
          <SummaryCard label={t('journal.losers')} value={summary.losing_trades} color="red" />
          <SummaryCard label="Slippage Avg" value={`${tca.avg_slippage_bps} bps`} color="blue" />
          <SummaryCard label="Fill Rate" value={`${tca.avg_fill_rate_pct}%`} color="cyan" />
          <SummaryCard label={t('journal.latencyAvg')} value={`${tca.avg_latency_ms}ms`} color="amber" />
        </div>

        {/* Agent & Strategy Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Agent P&L Chart */}
          {agentBreakdown.length > 0 && (
            <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="agent-pnl-chart">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-4 h-4 text-emerald-500" />
                <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('journal.pnlByAgent')}</h3>
              </div>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={agentBreakdown} layout="vertical" margin={{ left: 50 }}>
                    <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                      tickFormatter={v => `$${v.toFixed(0)}`} axisLine={false} tickLine={false} />
                    <YAxis type="category" dataKey="agent_id" tick={{ fill: '#e2e8f0', fontSize: 10, fontFamily: 'monospace' }}
                      axisLine={false} tickLine={false} width={50} />
                    <Tooltip contentStyle={{ background: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, fontFamily: 'monospace', fontSize: 11 }}
                      formatter={v => [`$${v.toFixed(2)}`, 'P&L']} />
                    <Bar dataKey="pnl" radius={[0, 4, 4, 0]}>
                      {agentBreakdown.map((e, i) => (
                        <Cell key={i} fill={e.pnl >= 0 ? '#10b981' : '#ef4444'} fillOpacity={0.8} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Strategy Breakdown */}
          <div className="bg-[#090B0F] border border-white/10 p-4" data-testid="strategy-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <Target className="w-4 h-4 text-emerald-500" />
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('journal.pnlByStrategy')}</h3>
            </div>
            <div className="space-y-2">
              {stratBreakdown.map(s => (
                <div key={s.strategy} className="flex items-center justify-between py-2 px-2 bg-[#11141D] border border-white/5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-emerald-400 bg-emerald-400/10 px-2 py-0.5">{s.strategy}</span>
                    <span className="text-[10px] font-mono text-slate-500">{s.trades} trades</span>
                  </div>
                  <span className={`text-xs font-mono font-bold ${s.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {s.pnl >= 0 ? '+' : ''}${s.pnl.toLocaleString()}
                  </span>
                </div>
              ))}
              {stratBreakdown.length === 0 && (
                <p className="text-slate-500 text-xs font-mono text-center py-4">{t('journal.noStrategyData')}</p>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="journal-filters">
          <Filter className="w-4 h-4 text-slate-500" />
          <select value={filterAgent} onChange={e => setFilterAgent(e.target.value)}
            className="bg-[#11141D] border border-white/10 text-white px-3 py-1.5 text-xs font-mono focus:outline-none focus:border-emerald-500/50"
            data-testid="filter-agent">
            <option value="">{t('journal.allAgents')}</option>
            {uniqueAgents.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={filterSymbol} onChange={e => setFilterSymbol(e.target.value)}
            className="bg-[#11141D] border border-white/10 text-white px-3 py-1.5 text-xs font-mono focus:outline-none focus:border-emerald-500/50"
            data-testid="filter-symbol">
            <option value="">{t('journal.allInstruments')}</option>
            {uniqueSymbols.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(filterAgent || filterSymbol) && (
            <button onClick={() => { setFilterAgent(''); setFilterSymbol(''); }}
              className="text-[10px] font-mono text-slate-400 hover:text-white px-2 py-1 border border-white/10" data-testid="clear-filters-btn">
              LIMPIAR
            </button>
          )}
        </div>

        {/* Trade List */}
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden" data-testid="trade-list">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" /> {t('journal.tradeHistory')}
            </h3>
          </div>

          {trades.length === 0 ? (
            <div className="p-12 text-center">
              <BookOpen className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500 text-sm font-mono">{t('journal.noTrades')}</p>
              <p className="text-slate-600 text-xs font-mono mt-1">{t('journal.noTradesSub')}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {trades.map((trade, idx) => {
                const isExpanded = expandedTrade === idx;
                const isBuy = trade.side === 'buy' || trade.side === 'BUY';
                const pnl = trade.pnl || 0;

                return (
                  <div key={trade.id || idx}>
                    {/* Trade Row */}
                    <button onClick={() => setExpandedTrade(isExpanded ? null : idx)}
                      className="w-full flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors text-left"
                      data-testid={`trade-row-${idx}`}>
                      <div className="flex items-center gap-2 w-20">
                        {isBuy
                          ? <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                          : <TrendingDown className="w-3.5 h-3.5 text-red-400" />}
                        <span className={`text-[10px] font-mono font-bold px-1.5 py-0.5 ${isBuy ? 'text-emerald-400 bg-emerald-400/10' : 'text-red-400 bg-red-400/10'}`}>
                          {isBuy ? 'BUY' : 'SELL'}
                        </span>
                      </div>
                      <span className="text-xs font-mono text-white font-medium w-24">{trade.symbol}</span>
                      <span className="text-[10px] font-mono text-slate-400 w-16">{trade.agent_id}</span>
                      <span className="text-xs font-mono text-white w-24 text-right">${trade.entry_price?.toFixed(trade.entry_price > 100 ? 2 : 4)}</span>
                      <span className="text-xs font-mono text-slate-400 w-8 text-center">&rarr;</span>
                      <span className="text-xs font-mono text-white w-24 text-right">${trade.exit_price?.toFixed(trade.exit_price > 100 ? 2 : 4)}</span>
                      <span className={`text-xs font-mono font-bold w-24 text-right ${pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                      </span>
                      <div className="flex-1" />
                      <span className="text-[10px] font-mono text-emerald-400/60 bg-emerald-400/5 px-2 py-0.5 hidden md:inline">
                        {trade.signal?.strategy || 'n/a'}
                      </span>
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                    </button>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <div className="px-4 pb-4 bg-[#060810]" data-testid={`trade-detail-${idx}`}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-3">
                          {/* Reasoning */}
                          <div className="bg-[#0A0D12] border border-white/5 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Zap className="w-4 h-4 text-amber-400" />
                              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('journal.signalReason')}</span>
                            </div>
                            <div className="space-y-2">
                              {(trade.reasoning || []).map((r, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <div className="w-1.5 h-1.5 bg-amber-400/60 mt-1.5 shrink-0" />
                                  <span className="text-xs text-slate-300">{r}</span>
                                </div>
                              ))}
                              <div className="mt-3 pt-3 border-t border-white/5">
                                <div className="flex justify-between text-[10px]">
                                  <span className="text-slate-500">{t('journal.confidence')}</span>
                                  <span className="text-white font-mono">{(trade.signal?.confidence * 100 || 0).toFixed(0)}%</span>
                                </div>
                                <div className="flex justify-between text-[10px] mt-1">
                                  <span className="text-slate-500">{t('journal.strength')}</span>
                                  <span className="text-white font-mono">{(trade.signal?.strength * 100 || 0).toFixed(0)}%</span>
                                </div>
                                <div className="flex justify-between text-[10px] mt-1">
                                  <span className="text-slate-500">{t('journal.strategy')}</span>
                                  <span className="text-emerald-400 font-mono">{trade.signal?.strategy}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* TCA Metrics */}
                          <div className="bg-[#0A0D12] border border-white/5 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <BarChart3 className="w-4 h-4 text-blue-400" />
                              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('journal.tcaMetrics')}</span>
                            </div>
                            <div className="space-y-3">
                              <TcaMetric label="Slippage" value={`${trade.tca?.slippage_bps} bps`} threshold={0.2} actual={trade.tca?.slippage_bps} />
                              <TcaMetric label="vs VWAP" value={`${trade.tca?.vwap_diff_bps >= 0 ? '+' : ''}${trade.tca?.vwap_diff_bps} bps`} threshold={3} actual={Math.abs(trade.tca?.vwap_diff_bps || 0)} />
                              <TcaMetric label="Fill Rate" value={`${trade.tca?.fill_rate_pct}%`} threshold={1} actual={100 - (trade.tca?.fill_rate_pct || 100)} />
                              <TcaMetric label={t('journal.latencyAvg').replace(' Avg', '')} value={`${trade.tca?.execution_latency_ms}ms`} threshold={50} actual={trade.tca?.execution_latency_ms} />
                              <TcaMetric label="Market Impact" value={`${trade.tca?.market_impact_bps} bps`} threshold={0.15} actual={trade.tca?.market_impact_bps} />
                            </div>
                          </div>

                          {/* Trade Details */}
                          <div className="bg-[#0A0D12] border border-white/5 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <Clock className="w-4 h-4 text-slate-400" />
                              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{t('journal.tradeDetails')}</span>
                            </div>
                            <div className="space-y-2">
                              <DetailRow label="Size" value={`$${trade.size?.toLocaleString()}`} />
                              <DetailRow label="Entry" value={`$${trade.entry_price?.toFixed(4)}`} />
                              <DetailRow label="Exit" value={`$${trade.exit_price?.toFixed(4)}`} />
                              <DetailRow label="Fees" value={`$${trade.fees?.toFixed(2)}`} />
                              <DetailRow label="Return" value={`${trade.return_pct?.toFixed(2)}%`}
                                valueClass={trade.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'} />
                              <DetailRow label="Entry Time" value={trade.entry_time ? new Date(trade.entry_time).toLocaleString() : '-'} />
                              <DetailRow label="Source" value={trade.signal?.source || 'engine'} />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  const colorMap = {
    emerald: 'text-emerald-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    cyan: 'text-cyan-400',
    amber: 'text-amber-400',
  };

  return (
    <div className="bg-[#090B0F] border border-white/10 p-3">
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</p>
      <p className={`text-lg font-black font-mono ${colorMap[color] || 'text-white'}`}>{value}</p>
    </div>
  );
}

function TcaMetric({ label, value, threshold, actual }) {
  const isGood = actual <= threshold;
  return (
    <div className="flex items-center justify-between">
      <span className="text-[10px] text-slate-500">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-xs font-mono text-white">{value}</span>
        <div className={`w-1.5 h-1.5 rounded-full ${isGood ? 'bg-emerald-500' : 'bg-amber-500'}`} />
      </div>
    </div>
  );
}

function DetailRow({ label, value, valueClass = 'text-white' }) {
  return (
    <div className="flex items-center justify-between text-[10px]">
      <span className="text-slate-500">{label}</span>
      <span className={`font-mono ${valueClass}`}>{value}</span>
    </div>
  );
}
